package pl.nickers.match3.ecs

import kotlinproject.composeapp.generated.resources.Res
import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonClassDiscriminator

/**
 * Data-driven definition of all entity types in the game. A [PieceSet] is a named
 * collection of [PieceDefinition]s loaded from a JSON resource.
 *
 * Each piece has a unique string id (used for matching, save games, level grids),
 * a [family] for behaviour categorisation and spawn-pool composition, a sprite
 * bundle, a list of external activations, lifecycle hooks ([onActivate], [onDestroyed]),
 * optional effect modifiers, and a typed [params] block whose shape depends on
 * the family.
 *
 * Matching is by piece id, not family: `ember_seed` matches `ember_seed`, not `moon_crystal`.
 * The family exists for things like "which pieces belong in the random spawn pool".
 */
@Serializable
data class PieceSet(
    val id: String,
    val name: String,
    val pieces: Map<String, PieceDefinition>,
) {
    /** Pieces with [PieceDefinition.spawnWeight] > 0, suitable for random board fill. */
    val spawnable: List<Pair<String, PieceDefinition>> by lazy {
        pieces.entries
            .filter { it.value.spawnWeight > 0.0 }
            .map { it.key to it.value }
    }

    /** Total weight across all spawnable pieces, for weighted random selection. */
    val totalSpawnWeight: Double by lazy {
        spawnable.sumOf { it.second.spawnWeight }
    }

    fun pieceIdsOf(family: Family): List<String> =
        pieces.entries.filter { it.value.family == family }.map { it.key }
}

@Serializable
data class PieceDefinition(
    val family: Family,
    val placement: Placement = Placement.GRID,
    val spawnWeight: Double = 0.0,
    val draggable: Boolean = true,
    val sprites: Sprites = Sprites(),
    val activations: List<Activation> = emptyList(),
    val onActivate: EffectStep? = null,
    val onDestroyed: EffectStep? = null,
    val modifiers: List<Modifier> = emptyList(),
    val params: PieceParams,
)

@Serializable
enum class Family {
    @SerialName("jelly") JELLY,
    @SerialName("bomb") BOMB,
    @SerialName("cross_bomb") CROSS_BOMB,
    @SerialName("laser") LASER,
    @SerialName("swirl") SWIRL,
    @SerialName("ice") ICE,
    @SerialName("infrastructure") INFRASTRUCTURE,
    @SerialName("powerup") POWERUP,
    @SerialName("familiar") FAMILIAR,
}

@Serializable
enum class Placement {
    @SerialName("grid") GRID,
    @SerialName("toolbar") TOOLBAR,
    @SerialName("overlay") OVERLAY,
}

/**
 * External triggers that can activate a piece.
 */
@Serializable
enum class Activation {
    @SerialName("MatchDetected") MATCH_DETECTED,
    @SerialName("SwapWithNeighbour") SWAP_WITH_NEIGHBOUR,
    @SerialName("DoubleClick") DOUBLE_CLICK,
    @SerialName("CaughtInAreaEffect") CAUGHT_IN_AREA_EFFECT,
    @SerialName("NeighbourMatch") NEIGHBOUR_MATCH,
    @SerialName("PowerUpButton") POWER_UP_BUTTON,
    @SerialName("InternalProgress") INTERNAL_PROGRESS,
}

/**
 * The set of effect kinds the engine can execute. Concrete behaviour lives in
 * the systems; the piece definition only names which effect runs on which hook.
 */
@Serializable
enum class EffectKind {
    @SerialName("Remove") REMOVE,
    @SerialName("AreaBlast3x3") AREA_BLAST_3X3,
    @SerialName("LineClear") LINE_CLEAR,
    @SerialName("RowClear") ROW_CLEAR,
    @SerialName("CollectColour") COLLECT_COLOUR,
    @SerialName("FullBoardClear") FULL_BOARD_CLEAR,
    @SerialName("DecrementLife") DECREMENT_LIFE,
    @SerialName("TransformInto") TRANSFORM_INTO,
    @SerialName("TransformToGoalType") TRANSFORM_TO_GOAL_TYPE,
}

/**
 * Effect modifiers attach extra semantics to a piece's [onActivate] step.
 * `GhostRepeat` means: after the first activation resolves and the board
 * settles, run [onActivate] a second time at the same grid position.
 */
@Serializable
enum class Modifier {
    @SerialName("GhostRepeat") GHOST_REPEAT,
}

/**
 * A lifecycle effect step. [selector] is consulted only by effects that
 * need a target piece (e.g. [EffectKind.TRANSFORM_INTO]).
 */
@Serializable
data class EffectStep(
    val effect: EffectKind,
    val selector: PieceSelector? = null,
)

/**
 * Names a piece (or pool of pieces) referenced by an effect such as
 * [EffectKind.TRANSFORM_INTO]. Polymorphic by discriminator `type`.
 */
@OptIn(ExperimentalSerializationApi::class)
@Serializable
@JsonClassDiscriminator("type")
sealed interface PieceSelector {
    /** Pick from the weighted spawn pool (pieces with `spawnWeight > 0`). */
    @Serializable
    @SerialName("spawnPool")
    data object SpawnPool : PieceSelector

    /** Pick uniformly from pieces of the given family. */
    @Serializable
    @SerialName("family")
    data class ByFamily(val family: Family) : PieceSelector

    /** Explicit allowlist of piece ids. */
    @Serializable
    @SerialName("list")
    data class Explicit(val pieces: List<String>) : PieceSelector

    /** Pick from the current level's goal target piece ids. */
    @Serializable
    @SerialName("goalTargets")
    data object GoalTargets : PieceSelector
}

/**
 * Sprite bundle. Different families consume different fields; the renderer
 * reads the field appropriate to the family. Grouping under one object keeps
 * sprite concerns isolated from runtime [PieceParams].
 *
 *   - [body]: static body sprite (most pieces).
 *   - [bodyByLife]: sprite array indexed by `life - 1`. Used for stateful
 *     pieces whose appearance varies with a life counter (currently ice cubes).
 *     The array length implies max life.
 *   - [faces]: optional face-overlay pool; one is picked at create-time.
 *   - [icon]: toolbar / overlay icon (power-ups, familiar).
 */
@Serializable
data class Sprites(
    val body: String? = null,
    val bodyByLife: List<String>? = null,
    val faces: List<String> = emptyList(),
    val icon: String? = null,
)

/**
 * Typed parameter bag whose concrete subtype is selected by the piece's
 * [Family]. Each variant carries only the fields its family actually needs;
 * effect handlers pattern-match on the subtype.
 */
@OptIn(ExperimentalSerializationApi::class)
@Serializable
@JsonClassDiscriminator("family")
sealed interface PieceParams {

    @Serializable
    @SerialName("jelly")
    data object Jelly : PieceParams

    @Serializable
    @SerialName("bomb")
    data object Bomb : PieceParams

    @Serializable
    @SerialName("cross_bomb")
    data object CrossBomb : PieceParams

    @Serializable
    @SerialName("laser")
    data class Laser(val direction: LaserDirection) : PieceParams

    @Serializable
    @SerialName("swirl")
    data object Swirl : PieceParams

    @Serializable
    @SerialName("ice")
    data class Ice(val initialLife: Int) : PieceParams

    @Serializable
    @SerialName("infrastructure")
    data object Infrastructure : PieceParams

    @Serializable
    @SerialName("powerup")
    data class PowerUp(val defaultUses: Int = 0) : PieceParams

    @Serializable
    @SerialName("familiar")
    data class Familiar(val threshold: Int) : PieceParams
}

/**
 * Loads piece sets from the compose-resource bundle and validates them.
 *
 * Validation rules (enforced on every load):
 *   - Every piece's `params.family` discriminator matches its top-level [PieceDefinition.family].
 *   - For `ice` family, `params.initialLife <= sprites.bodyByLife.size` (and `bodyByLife` is non-null).
 *   - `PieceSelector.Explicit` and selectors that reference families resolve to at least one piece.
 *   - `spawnWeight` is non-negative.
 *   - Piece ids are non-blank.
 */
object PieceSetLoader {

    private val jsonParser = Json {
        ignoreUnknownKeys = true
        classDiscriminator = "kind"  // overridden per-hierarchy by @JsonClassDiscriminator
    }

    const val DEFAULT_PATH = "files/piece_sets/default.json"

    suspend fun load(path: String = DEFAULT_PATH): PieceSet {
        val bytes = Res.readBytes(path)
        val parsed = jsonParser.decodeFromString<PieceSet>(bytes.decodeToString())
        validate(parsed)
        return parsed
    }

    fun parse(json: String): PieceSet {
        val parsed = jsonParser.decodeFromString<PieceSet>(json)
        validate(parsed)
        return parsed
    }

    fun validate(set: PieceSet) {
        require(set.id.isNotBlank()) { "PieceSet.id must not be blank" }
        require(set.pieces.isNotEmpty()) { "PieceSet '${set.id}' has no pieces" }

        for ((pieceId, piece) in set.pieces) {
            require(pieceId.isNotBlank()) { "PieceSet '${set.id}' contains a blank piece id" }
            require(piece.spawnWeight >= 0.0) {
                "Piece '$pieceId' has negative spawnWeight ${piece.spawnWeight}"
            }
            validateFamilyParamsAlignment(pieceId, piece)
            validateIceSprites(pieceId, piece)
        }

        // Cross-reference any explicit selectors so authors get a load-time error
        // instead of a silent NullPointerException at effect time.
        for ((pieceId, piece) in set.pieces) {
            val selectors = listOfNotNull(piece.onActivate?.selector, piece.onDestroyed?.selector)
            for (selector in selectors) {
                validateSelector(pieceId, selector, set)
            }
        }
    }

    /**
     * Hand-built fallback mirroring `composeResources/files/piece_sets/default.json`.
     *
     * Used by [pl.nickers.match3.GameViewModel] before async [load] completes, and by
     * tests that need a piece set without resource access. Must be kept in sync with
     * the JSON file; the values come straight from the file's contents.
     */
    fun default(): PieceSet {
        val jellyFaces = listOf(
            "face_1.png", "face_2.png", "face_3.png", "face_4.png", "face_5.png", "face_6.png",
        )
        fun jelly(body: String, weight: Double, faces: List<String> = jellyFaces) = PieceDefinition(
            family = Family.JELLY,
            spawnWeight = weight,
            sprites = Sprites(body = body, faces = faces),
            activations = listOf(Activation.MATCH_DETECTED),
            onActivate = EffectStep(EffectKind.REMOVE),
            params = PieceParams.Jelly,
        )

        val pieces = linkedMapOf(
            "ember_seed"    to jelly("jelly_1.png", 10.0),
            "moon_crystal"  to jelly("jelly_2.png", 10.0),
            "forest_herb"   to jelly("jelly_3.png", 10.0),
            "sun_pollen"    to jelly("jelly_4.png", 10.0),
            "dusk_petal"    to jelly("jelly_5.png", 10.0),
            "golden_nectar" to jelly("jelly_6.png", 10.0, faces = listOf("face_6.png")),
            "bomb" to PieceDefinition(
                family = Family.BOMB,
                spawnWeight = 0.3,
                sprites = Sprites(body = "bomb.png", faces = listOf("bomb_face_1.png", "bomb_face_2.png")),
                activations = listOf(Activation.SWAP_WITH_NEIGHBOUR, Activation.DOUBLE_CLICK, Activation.CAUGHT_IN_AREA_EFFECT),
                onActivate = EffectStep(EffectKind.AREA_BLAST_3X3),
                params = PieceParams.Bomb,
            ),
            "cross_bomb" to PieceDefinition(
                family = Family.CROSS_BOMB,
                sprites = Sprites(body = "cross_bomb.png", faces = listOf("bomb_face_1.png", "bomb_face_2.png")),
                activations = listOf(Activation.SWAP_WITH_NEIGHBOUR, Activation.DOUBLE_CLICK, Activation.CAUGHT_IN_AREA_EFFECT),
                onActivate = EffectStep(EffectKind.AREA_BLAST_3X3),
                modifiers = listOf(Modifier.GHOST_REPEAT),
                params = PieceParams.CrossBomb,
            ),
            "laser_horizontal" to PieceDefinition(
                family = Family.LASER,
                sprites = Sprites(body = "laser.png", faces = listOf("laser_face.png")),
                activations = listOf(Activation.SWAP_WITH_NEIGHBOUR, Activation.DOUBLE_CLICK, Activation.CAUGHT_IN_AREA_EFFECT),
                onActivate = EffectStep(EffectKind.LINE_CLEAR),
                params = PieceParams.Laser(LaserDirection.HORIZONTAL),
            ),
            "laser_vertical" to PieceDefinition(
                family = Family.LASER,
                sprites = Sprites(body = "laser.png", faces = listOf("laser_face.png")),
                activations = listOf(Activation.SWAP_WITH_NEIGHBOUR, Activation.DOUBLE_CLICK, Activation.CAUGHT_IN_AREA_EFFECT),
                onActivate = EffectStep(EffectKind.LINE_CLEAR),
                params = PieceParams.Laser(LaserDirection.VERTICAL),
            ),
            "colourful_swirl" to PieceDefinition(
                family = Family.SWIRL,
                sprites = Sprites(body = "swirl.png", faces = listOf("swirl_face.png")),
                activations = listOf(Activation.SWAP_WITH_NEIGHBOUR, Activation.DOUBLE_CLICK, Activation.CAUGHT_IN_AREA_EFFECT),
                onActivate = EffectStep(EffectKind.COLLECT_COLOUR),
                params = PieceParams.Swirl,
            ),
            "crystallized_amber" to PieceDefinition(
                family = Family.ICE,
                draggable = false,
                sprites = Sprites(bodyByLife = listOf("ice_1.png", "ice_2.png", "ice_3.png")),
                activations = listOf(Activation.NEIGHBOUR_MATCH, Activation.CAUGHT_IN_AREA_EFFECT),
                onActivate = EffectStep(EffectKind.DECREMENT_LIFE),
                onDestroyed = EffectStep(EffectKind.TRANSFORM_INTO, selector = PieceSelector.SpawnPool),
                params = PieceParams.Ice(initialLife = 3),
            ),
            "spawn_point" to PieceDefinition(
                family = Family.INFRASTRUCTURE,
                draggable = false,
                params = PieceParams.Infrastructure,
            ),
            "teleport_target" to PieceDefinition(
                family = Family.INFRASTRUCTURE,
                draggable = false,
                params = PieceParams.Infrastructure,
            ),
            "powerup_bomb" to PieceDefinition(
                family = Family.POWERUP,
                placement = Placement.TOOLBAR,
                draggable = false,
                sprites = Sprites(icon = "bomb.png"),
                activations = listOf(Activation.POWER_UP_BUTTON),
                onActivate = EffectStep(EffectKind.AREA_BLAST_3X3),
                params = PieceParams.PowerUp(),
            ),
            "powerup_row_bomb" to PieceDefinition(
                family = Family.POWERUP,
                placement = Placement.TOOLBAR,
                draggable = false,
                sprites = Sprites(icon = "bomb.png"),
                activations = listOf(Activation.POWER_UP_BUTTON),
                onActivate = EffectStep(EffectKind.ROW_CLEAR),
                params = PieceParams.PowerUp(),
            ),
            "powerup_targeted_remove" to PieceDefinition(
                family = Family.POWERUP,
                placement = Placement.TOOLBAR,
                draggable = false,
                sprites = Sprites(icon = "bomb.png"),
                activations = listOf(Activation.POWER_UP_BUTTON),
                onActivate = EffectStep(EffectKind.REMOVE),
                params = PieceParams.PowerUp(),
            ),
            "familiar" to PieceDefinition(
                family = Family.FAMILIAR,
                placement = Placement.OVERLAY,
                draggable = false,
                sprites = Sprites(icon = "face_6.png"),
                activations = listOf(Activation.INTERNAL_PROGRESS),
                onActivate = EffectStep(EffectKind.TRANSFORM_TO_GOAL_TYPE),
                params = PieceParams.Familiar(threshold = 10),
            ),
        )
        return PieceSet(id = "default", name = "Standard Match-3 Set", pieces = pieces)
            .also { validate(it) }
    }

    private fun validateFamilyParamsAlignment(pieceId: String, piece: PieceDefinition) {
        val ok = when (piece.params) {
            is PieceParams.Jelly -> piece.family == Family.JELLY
            is PieceParams.Bomb -> piece.family == Family.BOMB
            is PieceParams.CrossBomb -> piece.family == Family.CROSS_BOMB
            is PieceParams.Laser -> piece.family == Family.LASER
            is PieceParams.Swirl -> piece.family == Family.SWIRL
            is PieceParams.Ice -> piece.family == Family.ICE
            is PieceParams.Infrastructure -> piece.family == Family.INFRASTRUCTURE
            is PieceParams.PowerUp -> piece.family == Family.POWERUP
            is PieceParams.Familiar -> piece.family == Family.FAMILIAR
        }
        require(ok) {
            "Piece '$pieceId' family ${piece.family} does not match params variant ${piece.params::class.simpleName}"
        }
    }

    private fun validateIceSprites(pieceId: String, piece: PieceDefinition) {
        val params = piece.params
        if (params !is PieceParams.Ice) return
        val bodyByLife = piece.sprites.bodyByLife
        require(bodyByLife != null && bodyByLife.isNotEmpty()) {
            "Ice piece '$pieceId' must define sprites.bodyByLife"
        }
        require(params.initialLife in 1..bodyByLife.size) {
            "Ice piece '$pieceId' initialLife=${params.initialLife} is out of range " +
                    "1..${bodyByLife.size} (bodyByLife length)"
        }
    }

    private fun validateSelector(pieceId: String, selector: PieceSelector, set: PieceSet) {
        when (selector) {
            is PieceSelector.SpawnPool -> {
                require(set.spawnable.isNotEmpty()) {
                    "Piece '$pieceId' references SpawnPool but the set has no spawnable pieces"
                }
            }
            is PieceSelector.ByFamily -> {
                require(set.pieceIdsOf(selector.family).isNotEmpty()) {
                    "Piece '$pieceId' references family ${selector.family} but no piece has it"
                }
            }
            is PieceSelector.Explicit -> {
                require(selector.pieces.isNotEmpty()) {
                    "Piece '$pieceId' has empty Explicit selector"
                }
                for (refId in selector.pieces) {
                    require(refId in set.pieces) {
                        "Piece '$pieceId' selector references unknown piece id '$refId'"
                    }
                }
            }
            is PieceSelector.GoalTargets -> { /* resolved at runtime against level goals */ }
        }
    }
}
